import React, { useCallback, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { Flex, Popup, Selector } from '@leverege/ui-elements';
import { useValueChange } from '@leverege/ui-hooks';
import { ModelEditorFactory, useSharedContext } from '@leverege/ui-plugin';
import { R } from '@leverege/value-resolver';
import I18N from '@leverege/i18n';
import AttributeSelector from "./AttributeSelector.js";
import AttributeCondition from './AttributeCondition.js';
import Attributes from '../attribute/Attributes.js';
import AttributeComparatorEditors from "./comparator/AttributeComparators.js";
import AttributeValueSuggestorUtil from "./suggestor/Util.js";
import S from './AttributeConditionEditor.css';
import { prefixVariant } from "./Util.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = I18N.ns('ui-attributes.condition.AttributeConditionEditor');
export default function AttributeConditionEditor(props) {
  const {
    value,
    usage
  } = props;
  const attrName = AttributeCondition.getAttrName(value);
  const onChange = useValueChange(AttributeCondition, props);
  const {
    conditionOptions,
    objectType,
    delegate,
    relationship
  } = useSharedContext();
  const attribute = Attributes.getAttribute(attrName, objectType);
  const opts = conditionOptions?.attributeCondition;
  const attrs = useMemo(() => {
    if (opts?.attributeFilter) {
      return Attributes.getAttributesFor(objectType).filter(opts.attributeFilter);
    }
    return Attributes.getAttributesFor(objectType);
  }, [objectType, opts?.attributeFilter]);
  const comparatorFilter = attribute ? opts?.valueType?.[attribute.valueType]?.comparatorFilter || opts?.comparatorFilter : opts?.comparatorFilter;
  const getAvailComparators = useCallback(attr => {
    if (!attr) {
      return [];
    }
    let comparators = AttributeComparatorEditors.getComparatorsFor(attr.name, objectType) || [];
    comparators = comparatorFilter ? comparators.filter(comparatorFilter) : comparators;
    return comparators.sort(sorter).map(c => ({
      name: R(c.name),
      value: c.type,
      data: c
    }));
  }, [comparatorFilter, objectType]);
  const [availComparator, setAvailComparator] = useState(getAvailComparators(attribute));
  useEffect(() => {
    setAvailComparator(getAvailComparators(attribute));
  }, [attribute, getAvailComparators]);
  const comparator = useMemo(() => {
    const c = AttributeCondition.getComparator(value);
    if (c) {
      return c;
    }
    return availComparator.find(comp => {
      return comp.data.isPreferred?.(usage) !== false;
    })?.data?.create?.() || null; // availComparator[0]?.data?.create?.() || null
  }, [value, availComparator, usage]);
  const onAttrChange = useCallback(evt => {
    const attr = Attributes.getAttribute(evt.value, objectType);
    const aC = getAvailComparators(attr);
    setAvailComparator(aC);
    const c = aC.find(c => c.value === comparator?.type) || aC?.[0]?.data?.create?.();
    onChange({
      data: {
        [evt.data]: 'attrName',
        setComparator: 'comparator'
      },
      value: {
        attrName: evt.value,
        comparator: c || null
      }
    });
  }, [comparator?.type, onChange, objectType, getAvailComparators]);
  const onComparatorChange = useCallback(evt => {
    const c = availComparator?.find(c => c.value === evt?.value)?.data;
    let nValue = c?.create();
    if (value?.comparator?.type !== nValue?.type) {
      const Model = c;
      nValue = Model?.takeValuesFrom?.(nValue, value?.comparator) || nValue;
    }
    onChange({
      ...evt,
      value: nValue
    });
  }, [value?.comparator, onChange, availComparator]);
  const suggestor = useMemo(() => {
    if (!attribute) {
      return null;
    }
    return AttributeValueSuggestorUtil.getAttributeValueSuggestorFor(attribute, {
      attribute,
      conditionOptions,
      objectType,
      delegate,
      relationship
    });
  }, [attribute, delegate, conditionOptions, objectType, relationship]);
  return /*#__PURE__*/_jsxs(Flex, {
    style: {
      flexWrap: usage === 'table' ? 'wrap' : 'nowrap'
    },
    className: S.selectorContainer,
    variant: "rowS",
    align: "center",
    children: [usage !== 'table' && /*#__PURE__*/_jsx(AttributeSelector, {
      className: S.selector,
      eventData: "setAttrName",
      value: attrName,
      values: attrs,
      onChange: onAttrChange
    }), /*#__PURE__*/_jsx(Selector, {
      iconLocation: "post",
      disabled: !attrName,
      checkStyle: null,
      style: usage === 'table' ? {
        width: '100%'
      } : null,
      variant: prefixVariant('conditionSelector', usage, 'inline'),
      toggleVariant: prefixVariant('conditionSelector', usage, 'inline'),
      values: availComparator,
      value: comparator?.type,
      onChange: onComparatorChange,
      eventData: "setComparator",
      renderer: itemRenderer,
      title: renderer
    }), attrName && /*#__PURE__*/_jsx("div", {
      className: classnames(S.blurHandler, usage ? S[usage] : undefined),
      onKeyDown: evt => {
        evt.stopPropagation();
      },
      children: ModelEditorFactory.create(comparator, {
        usage,
        onChange,
        eventData: 'setComparator',
        attribute,
        suggestor,
        variant: prefixVariant('comparator', usage)
      })
    })]
  });
}
function itemRenderer(item, title) {
  return /*#__PURE__*/_jsx(Popup.Item, {
    value: item.value,
    icon: item?.data?.icon,
    children: title
  });
}
function renderer(val) {
  return val?.name || t('is');
}
function sorter(a, b) {
  const sortA = R(a.layout)?.sort || R(a.name) || R(a.type) || '_z';
  const sortB = R(b.layout)?.sort || R(b.name) || R(b.type) || '_z';
  return sortA.localeCompare(sortB);
}